import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/about', label: 'About' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      data-testid="theme-toggle"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      className="text-foreground hover:text-primary hover:bg-primary/10"
    >
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}

function NavLinkItem({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `text-sm font-medium transition-colors duration-200 ${
          isActive
            ? 'text-primary'
            : 'text-muted-foreground hover:text-foreground'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-12">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <div className="flex items-center">
            <span className="text-xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              thought
            </span>
            <span className="text-xl font-bold tracking-tight text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
              basics
            </span>
            <span className="text-primary text-xl font-bold">.</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <NavLinkItem key={link.to} {...link} />
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link to="/contact" className="hidden md:block">
            <Button
              data-testid="header-cta"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-sm font-medium"
            >
              Get in touch
            </Button>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-background border-border">
              <nav className="flex flex-col gap-6 mt-8" data-testid="mobile-nav">
                {navLinks.map((link) => (
                  <NavLinkItem key={link.to} {...link} onClick={() => setOpen(false)} />
                ))}
                <Link to="/contact" onClick={() => setOpen(false)}>
                  <Button
                    data-testid="mobile-cta"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Get in touch
                  </Button>
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
